import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchCustomersData } from "../../store/customers/actions";
import { fetchBookingsData } from 'store/bookings/actions';
import "./bookings.css";
import moment from 'moment';

const DEFAULT_PAGE_SIZE = 10;

const Bookings = props => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, loading, error } = useSelector(state => state.Bookings);

  useEffect(() => {
    dispatch(fetchBookingsData({ page: pageIndex + 1, limit: pageSize }));
  }, [dispatch, pageIndex, pageSize]);

  const columns = useMemo(
    () => [
      {
        header: 'Booking Id',
        accessorKey: 'bookingId',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Category Type',
        accessorKey: 'categoryType',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Customer',
        accessorKey: 'customer',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Vehicle Type',
        accessorKey: 'vehicleType',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Payment Status',
        accessorKey: 'payment_status',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const status = row?.original?.payment_status?.toLowerCase();

          let badgeClass = "badge-secondary";
          if (status === "paid") badgeClass = "badge-success";
          else if (status === "pending") badgeClass = "badge-warning";

          return (
            <span className={`badge ${badgeClass}`}>
              {row?.original?.payment_status?.toUpperCase() || "N/A"}
            </span>
          );
        }
      },
      {
        header: 'Status',
        accessorKey: 'status',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const status = row?.original?.status?.toLowerCase();

          let badgeClass = "badge-secondary";
          let Status = "Pending";

          switch (status) {
            case "pending":
              badgeClass = "badge-secondary"; // Grey
              Status = "Pending";
              break;
            case "ongoing":
              badgeClass = "badge-primary"; // Blue
              Status = "Ongoing";
              break;
            case "arrived":
              badgeClass = "badge-info"; // Light blue
              Status = "Arrived";
              break;
            case "start":
              badgeClass = "badge-dark"; // Black
              Status = "Started";
              break;
            case "complete":
              badgeClass = "badge-success"; // Green
              Status = "Completed";
              break;
            case "cancel":
              badgeClass = "badge-danger"; // Red
              Status = "Cancelled";
              break;
            case "expired":
              badgeClass = "badge-warning"; // Yellow
              Status = "Expired";
              break;
            case "scheduled":
              badgeClass = "badge-primary"; // Blue
              Status = "Scheduled";
              break;
            case "scheduled_accept":
              badgeClass = "badge-info"; // Light blue
              Status = "Scheduled Accepted";
              break;
            case "hooked":
              badgeClass = "badge-dark"; // Black
              Status = "Hooked";
              break;
            case "loading_proccess":
              badgeClass = "badge-warning"; // Yellow
              Status = "Loading Process";
              break;
            case "unloading_proccess":
              badgeClass = "badge-warning"; // Yellow
              Status = "Unloading Process";
              break;
            default:
              badgeClass = "badge-secondary";
              Status = status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending";
          }

          return (
            <span className={`badge ${badgeClass}`}>
              {Status}
            </span>
          );
        }
      },
      {
        header: 'Amount',
        accessorKey: 'amount',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Action',
        id: 'action',
        cell: ({ row }) => {  
          const bookingId = row.original?.bookingId;
          return (
            <button
              className="btn btn-sm btn-success"
              onClick={() => navigate(`/bookings/${bookingId}`)}
            >
              <i className="mdi mdi-eye"></i> View
            </button>
          );
        }
      }
    ],
    [navigate]
  );

  const transformedData = useMemo(() => {
    if (!data?.data) return [];
    const filteredData = data.data.filter(item => item.booking);
    return filteredData.map(item => ({
      bookingId: item.booking?.id || "N/A",
      categoryType: item?.booking?.type === "taxi" ? 'Ride Hailing' : (item?.booking?.type || "N/A"),
      customer: item.customer?.name || "Client supprimé",
      vehicleType: item.vehicle_type?.type_name || "Type supprimé",
      payment_status: item.booking?.payment_status || "N/A",
      status: item.booking?.status || "N/A",
      amount: item.booking?.total_amount || 0.00,
      createdAt: moment(item?.booking?.created_at).format("DD-MM-YY hh:mm A") || "N/A",
    }));
  }, [data]);

  const totalRecords = data?.total ?? transformedData?.length ?? 0;

  const handlePaginationChange = (newPageIndex, newPageSize) => {
    setPageIndex(newPageIndex);
    setPageSize(newPageSize);
  };

  document.title = "Bookings | Moeen";
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Bookings" />
        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            isCustomPageSize={true}
            SearchPlaceholder={`Search in ${totalRecords} results.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
            serverSidePagination={true}
            totalRowCount={totalRecords}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPaginationChange={handlePaginationChange}
          />
        )}
      </div>
    </div>
  );
};

Bookings.propTypes = {
  t: PropTypes.any
};

export default Bookings;
